<template>
  <div class="space-y-4">
    <!-- Controls -->
    <div class="flex flex-wrap items-end gap-3">
      <label class="flex flex-col text-sm">
        <span class="mb-1 text-gray-600 dark:text-gray-300">Time range</span>
        <select v-model.number="days" :class="inputClass" @change="reload">
          <option :value="1">Last 24 hours</option>
          <option :value="7">Last 7 days</option>
          <option :value="14">Last 14 days</option>
          <option :value="30">Last 30 days</option>
          <option :value="90">Last 90 days</option>
          <option :value="365">Last 12 months</option>
        </select>
      </label>

      <label class="flex flex-col text-sm">
        <span class="mb-1 text-gray-600 dark:text-gray-300">Organisation</span>
        <select v-model="org" :class="inputClass" @change="reload">
          <option value="">All organisations</option>
          <option v-for="o in organisations" :key="o.id" :value="String(o.id)">{{ o.name }}</option>
        </select>
      </label>

      <button class="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50" :disabled="loading" @click="reload">
        Refresh
      </button>
      <button class="px-3 py-2 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              @click="showExplain = true">
        How is this calculated?
      </button>
      <span v-if="loading" class="text-sm text-gray-500 dark:text-gray-400 pb-2">Loading…</span>
      <span v-if="error" class="text-sm text-red-600 dark:text-red-400 pb-2">{{ error }}</span>
    </div>

    <!-- Health banner -->
    <div v-if="healthWarnings.length"
         class="p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200">
      <p class="font-medium mb-1">Check before trusting these numbers</p>
      <ul class="list-disc list-inside text-sm space-y-0.5">
        <li v-for="w in healthWarnings" :key="w">{{ w }}</li>
      </ul>
    </div>

    <!-- KPIs -->
    <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <div v-for="card in kpiCards" :key="card.label"
           class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ card.label }}</p>
        <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ card.value }}</p>
        <p v-if="card.hint" class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ card.hint }}</p>
      </div>
    </div>

    <!-- Connectivity chart -->
    <div class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex items-baseline justify-between mb-2">
        <h2 class="text-base font-semibold text-gray-800 dark:text-gray-100">Bikes online</h2>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ granularity === 'hour' ? 'Bikes reporting per hour' : 'Distinct bikes reporting per day' }}
        </span>
      </div>
      <div ref="onlineChart" style="width: 100%; height: 320px;"></div>
    </div>

    <!-- Cost model -->
    <div class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <h2 class="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1">Cost model</h2>
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Rates are applied in the browser so pricing assumptions can be varied without a deploy.
        Cellular data is a one-off cost for the traffic in this period; storage is the recurring
        monthly cost of keeping the telemetry this period produced.
      </p>
      <div class="flex flex-wrap items-end gap-4">
        <label class="flex flex-col text-sm">
          <span class="mb-1 text-gray-600 dark:text-gray-300">Cellular data (€ per MB)</span>
          <input v-model.number="rates.dataPerMb" type="number" step="0.0001" min="0"
                 :class="[inputClass, 'w-40']" />
        </label>
        <label class="flex flex-col text-sm">
          <span class="mb-1 text-gray-600 dark:text-gray-300">Storage (€ per GB-month)</span>
          <input v-model.number="rates.storagePerGbMonth" type="number" step="0.001" min="0"
                 :class="[inputClass, 'w-40']" />
        </label>
        <div class="text-sm">
          <p class="text-gray-500 dark:text-gray-400">Cellular data, this period</p>
          <p class="text-xl font-semibold text-gray-900 dark:text-white">{{ money(fleetDataCost) }}</p>
        </div>
        <div class="text-sm">
          <p class="text-gray-500 dark:text-gray-400">Storage, per month</p>
          <p class="text-xl font-semibold text-gray-900 dark:text-white">{{ money(fleetStorageMonthlyCost) }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">for the {{ number(totalStorageGb, 2) }} GB written in this period</p>
        </div>
        <div class="text-sm">
          <p class="text-gray-500 dark:text-gray-400">Per bike per month</p>
          <p class="text-xl font-semibold text-gray-900 dark:text-white">{{ money(avgCostPerBikeMonth) }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">averaged over {{ activeBikeCount }} reporting bikes</p>
        </div>
      </div>
    </div>

    <!-- Per-bike table -->
    <div class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 class="text-base font-semibold text-gray-800 dark:text-gray-100">
          Per-bike detail
          <span class="text-sm font-normal text-gray-500 dark:text-gray-400">({{ visibleBikes.length }} of {{ bikes.length }})</span>
        </h2>
        <div class="flex flex-wrap items-center gap-2">
          <input v-model="search" type="text" placeholder="Search frame, IMEI or ICCID"
                 :class="[inputClass, 'w-64']" />
          <select v-model="statusFilter" :class="inputClass">
            <option value="all">All bikes</option>
            <option value="online">Reported in period</option>
            <option value="never">Never reported</option>
            <option value="quiet">Gone quiet ({{ goneQuietDays }}+ days)</option>
            <option value="nosim">No SIM linked</option>
          </select>
          <button class="px-3 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                  @click="exportCsv">
            Export CSV
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-700 dark:text-gray-300">
          <thead class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th v-for="col in columns" :key="col.key"
                  class="px-3 py-2 cursor-pointer select-none whitespace-nowrap hover:text-gray-700 dark:hover:text-gray-200"
                  :class="col.numeric ? 'text-right' : ''"
                  @click="sortBy(col.key)">
                {{ col.label }}
                <span v-if="sort.key === col.key">{{ sort.desc ? '▾' : '▴' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bike in visibleBikes" :key="bike.imei"
                class="border-b last:border-0 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                @click="openDetail(bike)">
              <td class="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                {{ bike.frame_number || '—' }}
                <span v-if="bike.is_out_of_service" class="ml-1 text-xs text-gray-500 dark:text-gray-400">(out of service)</span>
              </td>
              <td class="px-3 py-2 font-mono text-xs">{{ bike.imei }}</td>
              <td class="px-3 py-2">{{ bike.organisation || '—' }}</td>
              <td class="px-3 py-2 font-mono text-xs">
                {{ bike.iccid || '—' }}
                <span v-if="bike.sim_link_source === 'manual'" class="text-xs text-gray-500 dark:text-gray-400">(manual)</span>
              </td>
              <td class="px-3 py-2 text-right">{{ bike.days_online }}</td>
              <td class="px-3 py-2 text-right">{{ bike.hours_online }}</td>
              <td class="px-3 py-2 text-right">{{ number(bike.data_mb, 2) }}</td>
              <td class="px-3 py-2 text-right">{{ number(bike.storage_mb, 2) }}</td>
              <td class="px-3 py-2 text-right">
                {{ bike.quota_remaining_mb === null ? '—' : number(bike.quota_remaining_mb, 1) }}
              </td>
              <td class="px-3 py-2 text-right font-medium">{{ money(costOf(bike)) }}</td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span :class="statusClass(bike)">{{ statusLabel(bike) }}</span>
              </td>
            </tr>
            <tr v-if="!visibleBikes.length">
              <td :colspan="columns.length" class="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                No bikes match the current filters.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Detail drawer -->
    <div v-if="detail" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" @click.self="closeDetail">
      <div class="bg-white dark:bg-gray-800 border border-transparent dark:border-gray-700 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-5">
        <div class="flex items-start justify-between mb-3">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ detail.frame_number || detail.imei }}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 font-mono">
              IMEI {{ detail.imei }}
              <span v-if="detail.sim"> · ICCID {{ detail.sim.iccid }}</span>
            </p>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Uploads every {{ Math.round(detail.upload_interval_s / 60) }} min ·
              last server contact {{ formatDate(detail.last_upload_timestamp) }}
            </p>
          </div>
          <button class="px-3 py-1 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                  @click="closeDetail">
            Close
          </button>
        </div>
        <div ref="detailChart" style="width: 100%; height: 300px;"></div>
      </div>
    </div>

    <!-- Explain drawer -->
    <div v-if="showExplain" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         @click.self="showExplain = false">
      <div class="bg-white dark:bg-gray-800 border border-transparent dark:border-gray-700 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
        <div class="flex items-start justify-between gap-4 mb-5">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              How every number on this page is calculated
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Figures below reflect your current selection: {{ rangeLabel }},
              {{ org ? selectedOrgName : 'all organisations' }}.
            </p>
          </div>
          <button class="px-3 py-1 rounded shrink-0 bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                  @click="showExplain = false">
            Close
          </button>
        </div>

        <section v-for="group in explainGroups" :key="group.title" class="mb-6">
          <h4 class="text-base font-semibold text-gray-800 dark:text-gray-100">{{ group.title }}</h4>
          <p v-if="group.intro" class="text-sm text-gray-600 dark:text-gray-300 mt-1">{{ group.intro }}</p>

          <div v-for="item in group.items" :key="item.title"
               class="mt-3 pl-3 border-l-2 border-gray-200 dark:border-gray-600">
            <p class="font-medium text-sm text-gray-900 dark:text-white">{{ item.title }}</p>
            <p v-for="(paragraph, i) in item.body" :key="i"
               class="text-sm text-gray-600 dark:text-gray-300 mt-1">{{ paragraph }}</p>
            <code v-if="item.formula"
                  class="block mt-2 text-xs font-mono bg-gray-100 text-gray-800 rounded px-2 py-1.5 dark:bg-gray-900 dark:text-gray-200">
              {{ item.formula }}
            </code>
            <p v-if="item.caveat" class="text-xs text-amber-700 dark:text-amber-300 mt-1.5">
              Watch out: {{ item.caveat }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
// ECharts is loaded per page from dist/echarts.bundle.js (see iot_usage.html) and
// exposed as window.echarts, so it stays out of the main bundle every page pays for.

const GONE_QUIET_DAYS = 7;
const MB_PER_GB = 1024;
const DAYS_PER_MONTH = 30;

const ONLINE_COLOR = '#2563EB';
const DATA_COLOR = '#F59E0B';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const INPUT_CLASS =
  'p-2 rounded border border-gray-300 bg-white text-gray-900 placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ' +
  'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400';

const RANGE_LABELS = {
  1: 'last 24 hours',
  7: 'last 7 days',
  14: 'last 14 days',
  30: 'last 30 days',
  90: 'last 90 days',
  365: 'last 12 months',
};

export default {
  name: 'AppIotUsage',
  data() {
    return {
      loading: false,
      error: '',
      days: 30,
      org: '',
      organisations: [],
      granularity: 'day',
      series: [],
      kpis: {},
      health: {},
      bikes: [],
      search: '',
      statusFilter: 'all',
      sort: { key: 'days_online', desc: true },
      // Starting point: the 1NCE lifetime flat is roughly €10 for 500 MB.
      rates: { dataPerMb: 0.02, storagePerGbMonth: 0.10 },
      detail: null,
      showExplain: false,
      goneQuietDays: GONE_QUIET_DAYS,
      onlineChart: null,
      detailChart: null,
      columns: [
        { key: 'frame_number', label: 'Frame' },
        { key: 'imei', label: 'IMEI' },
        { key: 'organisation', label: 'Organisation' },
        { key: 'iccid', label: 'ICCID' },
        { key: 'days_online', label: 'Days online', numeric: true },
        { key: 'hours_online', label: 'Hours online', numeric: true },
        { key: 'data_mb', label: 'Data MB', numeric: true },
        { key: 'storage_mb', label: 'Storage MB', numeric: true },
        { key: 'quota_remaining_mb', label: 'Quota left MB', numeric: true },
        { key: 'cost', label: 'Cost (data + storage/mo)', numeric: true },
        { key: 'status', label: 'Status' },
      ],
    };
  },
  computed: {
    inputClass() {
      return INPUT_CLASS;
    },
    kpiCards() {
      const k = this.kpis;
      return [
        { label: 'Bikes seen', value: this.number(k.bikes_seen, 0), hint: `of ${this.number(k.bikes_total, 0)} modules` },
        { label: 'Never reported', value: this.number(k.bikes_never_seen, 0), hint: 'in this period' },
        { label: 'Peak concurrent', value: this.number(k.peak_concurrent, 0), hint: this.formatDate(k.peak_at) },
        { label: 'Cellular data', value: `${this.number(k.total_data_mb, 1)} MB`, hint: '1NCE, whole period' },
        { label: 'Storage used', value: `${this.number(k.total_storage_mb, 1)} MB`, hint: this.storageHint },
        { label: 'Datapoints', value: this.number(k.total_datapoints, 0), hint: 'written to InfluxDB' },
      ];
    },
    storageHint() {
      // Naming the real disk total makes clear the estimate is a split of a
      // measured number, not a guess at one.
      const measured = this.health.measured_disk_mb;
      if (!measured) return 'estimated in InfluxDB';
      return `share of ${this.number(measured, 0)} MB measured on disk`;
    },
    healthWarnings() {
      const h = this.health;
      // Before the first response lands every field is undefined, which would
      // otherwise read as "nothing is configured".
      if (!Object.keys(h).length) return [];
      const warnings = [];
      if (h.sims_total === 0) {
        warnings.push('No SIMs have been synced from 1NCE yet, so all data volume shows as zero. Run the "1NCE SIM Inventory Sync" task.');
      } else if (h.sims_unlinked > 0) {
        warnings.push(`${h.sims_unlinked} of ${h.sims_total} SIMs could not be matched to a FLEET module by IMEI. Their data volume is not attributed to any bike.`);
      }
      if (h.modules_without_sim > 0) {
        warnings.push(`${h.modules_without_sim} FLEET modules have no SIM linked, so they show no cellular data volume.`);
      }
      if (!h.calibration_measured_at) {
        warnings.push('Storage has never been calibrated against real InfluxDB disk usage. Run the "IoT Connectivity Rollup" task.');
      } else if (h.calibration_buckets_fallback > 0) {
        warnings.push(`${h.calibration_buckets_fallback} of ${h.calibration_buckets_total} organisation buckets could not be measured against real InfluxDB disk usage, so their storage figures use the fallback rate. This is normal for organisations that have never received telemetry.`);
      }
      return warnings;
    },
    filteredBikes() {
      const needle = this.search.trim().toLowerCase();
      return this.bikes.filter((bike) => {
        if (needle) {
          const haystack = `${bike.frame_number} ${bike.imei} ${bike.iccid} ${bike.serial_nr}`.toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
        switch (this.statusFilter) {
          case 'online': return !bike.never_seen;
          case 'never': return bike.never_seen;
          case 'quiet': return bike.gone_quiet;
          case 'nosim': return !bike.iccid;
          default: return true;
        }
      });
    },
    visibleBikes() {
      const { key, desc } = this.sort;
      const direction = desc ? -1 : 1;
      return [...this.filteredBikes].sort((a, b) => {
        const av = this.sortValue(a, key);
        const bv = this.sortValue(b, key);
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        return av > bv ? direction : -direction;
      });
    },
    fleetDataCost() {
      return this.filteredBikes.reduce((sum, bike) => sum + this.dataCostOf(bike), 0);
    },
    fleetStorageMonthlyCost() {
      return this.filteredBikes.reduce((sum, bike) => sum + this.storageMonthlyCostOf(bike), 0);
    },
    totalStorageGb() {
      return this.filteredBikes.reduce((sum, bike) => sum + (bike.storage_mb || 0), 0) / MB_PER_GB;
    },
    activeBikeCount() {
      return this.filteredBikes.filter((b) => !b.never_seen).length;
    },
    avgCostPerBikeMonth() {
      // Data is traffic during the period, so it scales to a month. Storage is
      // already a monthly rate on the data the period produced and must not be
      // rescaled, or a 12-month view would divide it away.
      const active = this.activeBikeCount;
      if (!active || !this.days) return 0;
      const monthlyData = this.fleetDataCost * (DAYS_PER_MONTH / this.days);
      return (monthlyData + this.fleetStorageMonthlyCost) / active;
    },
    rangeLabel() {
      return RANGE_LABELS[this.days] || `last ${this.days} days`;
    },
    selectedOrgName() {
      const match = this.organisations.find((o) => String(o.id) === String(this.org));
      return match ? match.name : 'all organisations';
    },
    explainGroups() {
      const h = this.health;
      // Quoting the live calibration turns the storage explanation from theory
      // into something the reader can check against the KPI on screen.
      const calibration = h.calibration_bytes_per_datapoint
        ? `Right now that rate averages ${this.number(h.calibration_bytes_per_datapoint, 2)} bytes per datapoint across ${this.number(h.calibration_buckets_total - h.calibration_buckets_fallback, 0)} measured bucket(s), which together hold ${this.number(h.measured_disk_mb, 0)} MB on disk.`
        : 'Storage has not been calibrated against real disk usage yet, so every figure currently uses the fallback rate.';

      return [
        {
          title: 'What counts as "online"',
          intro: 'Everything on this page comes from one signal and three nightly jobs. Once those are clear, every number below follows from them.',
          items: [
            {
              title: 'The heartbeat signal, not GPS',
              body: [
                'A FLEET module sends signal 0x0004, its serial number, with every single upload, whether or not it has a GPS fix. The presence of that signal is what this dashboard treats as "the bike was online".',
                'Counting GPS positions instead would undercount badly: a bike parked in an underground garage still uploads over the mobile network, it simply cannot see satellites.',
              ],
            },
            {
              title: 'The nightly connectivity rollup',
              body: [
                'Once a night a task scans InfluxDB and writes one row per bike per day. Each row records which of the 24 hours the bike sent a heartbeat (as a 24-bit mask, one bit per hour), how many datapoints it wrote in total, and its estimated storage footprint.',
                'The dashboard only ever reads those pre-computed daily rows, never raw InfluxDB. That is why the page loads instantly, and also why a day stays missing until the rollup has run for it.',
              ],
              caveat: "InfluxDB timestamps come from the bike's own clock, not from the moment the server received the upload. A module with a broken clock lands in the wrong hour, or in no day at all.",
            },
            {
              title: 'The nightly 1NCE syncs',
              body: [
                'The SIM inventory sync pulls the SIM list and links each SIM to a FLEET module by IMEI. 1NCE reports a 16-digit IMEISV while we store a 15-digit IMEI, so the match uses the first 14 digits, which identify the device in both formats. A link you set by hand is never overwritten.',
                'The data usage sync then fetches one day of traffic and the remaining lifetime quota for every linked SIM.',
              ],
              caveat: 'Traffic from a SIM that is not linked to a module belongs to no bike and is missing from every data figure here. The banner at the top of the page tells you how many SIMs that affects.',
            },
          ],
        },
        {
          title: 'The six KPI cards',
          intro: 'All six follow the time range and organisation selected at the top of the page.',
          items: [
            {
              title: 'Bikes seen',
              body: [
                'The number of distinct bikes that sent at least one heartbeat inside the range. A bike online for a single hour three weeks ago counts exactly the same as one online continuously.',
                'The "of N modules" underneath is every FLEET module in the database, narrowed to the selected organisation, whether it ever reported or not.',
              ],
              formula: 'bikes seen = COUNT(DISTINCT bike with any day where hours online > 0)',
            },
            {
              title: 'Never reported',
              body: [
                'Modules in the database minus bikes seen. These produced no telemetry at all during the range: never installed, sitting in a warehouse, out of service, or broken.',
                'It depends on the range, so shortening the range pushes this number up.',
              ],
              formula: 'never reported = modules in scope − bikes seen',
            },
            {
              title: 'Peak concurrent',
              body: [
                'The largest number of different bikes that were online within one and the same hour, anywhere in the range, together with the timestamp of that hour.',
                'It comes from the hour masks: for every hour of every day we count how many bikes have that bit set, and keep the maximum. It is always computed hourly, even when the chart is showing days.',
              ],
              caveat: '"Concurrent" means within the same hour, not at the same second. A bike uploading every 15 minutes and one uploading once an hour both occupy the whole hour.',
            },
            {
              title: 'Cellular data',
              body: [
                'The daily traffic 1NCE reports for every SIM linked to a bike in scope, summed over the range. This is real billed mobile traffic measured by the operator, not an estimate.',
                'It covers everything on the wire: telemetry payload, protocol overhead, retries and reconnects. That is why it is always larger than the raw payload size you might expect.',
              ],
              formula: 'cellular data = Σ daily SIM volume over the range (linked SIMs only)',
            },
            {
              title: 'Storage used',
              body: [
                'How much InfluxDB disk the telemetry produced during this range occupies. This is the one estimated figure on the page, so it is worth reading closely.',
                'InfluxDB tells us exactly how many bytes each organisation bucket occupies on disk, but not how those bytes split across individual bikes, because compression works across the whole shard. So we divide: bucket disk size ÷ datapoints in that bucket gives a bytes-per-datapoint rate, and each bike is charged its own datapoint count at that rate.',
                calibration,
                'The fleet total is therefore anchored to a measured number. Only the split between two individual bikes is an assumption, namely that all datapoints compress about equally.',
              ],
              formula: 'bike storage = bike datapoints × (bucket disk bytes ÷ bucket datapoints)',
              caveat: 'An organisation whose bucket cannot be measured falls back to a fixed 6 bytes per datapoint (INFLUX_BYTES_PER_POINT). That is normal for organisations that have never received telemetry.',
            },
            {
              title: 'Datapoints',
              body: [
                'Every individual field value the bikes in scope wrote to InfluxDB during the range, across all signals rather than just the heartbeat. One upload usually carries many signals and therefore many datapoints.',
                'This is the raw quantity the storage estimate is built on, shown so you can sanity-check it.',
              ],
            },
          ],
        },
        {
          title: 'The "Bikes online" chart',
          items: [
            {
              title: 'Hourly up to 14 days, daily beyond that',
              body: [
                'Short ranges are drawn hourly; longer ones switch to daily, because an hourly series over months is neither readable nor cheap to load. The label above the chart tells you which mode you are in.',
                'An hourly bar is the number of bikes that sent a heartbeat during that hour. A daily bar is the number of distinct bikes online at any point that day, so a bike counts once whether it was online for one hour or all 24.',
              ],
              caveat: 'Do not compare the height of a daily bar against an hourly one; they answer different questions and daily bars are always the higher of the two.',
            },
            {
              title: 'Flat stretches',
              body: [
                'A run of zeros means either no bike reported, or the rollup has not processed those days yet. If a recent stretch is flat, check the task page before concluding the fleet went dark.',
              ],
            },
          ],
        },
        {
          title: 'The cost model',
          intro: 'Rates are applied in your browser so pricing assumptions can be varied without a deploy; nothing you type is saved. All three figures follow the table filters, so a search term or status filter narrows them too.',
          items: [
            {
              title: 'Cellular data, this period',
              body: [
                "Each bike's traffic in the range multiplied by your €/MB rate, summed. It is a one-off cost for traffic that actually happened in the selected window, so it grows as you widen the range.",
                'The 0.02 €/MB default comes from the 1NCE lifetime flat, roughly €10 for 500 MB.',
              ],
              formula: 'data cost = Σ (bike data MB × rate €/MB)',
            },
            {
              title: 'Storage, per month',
              body: [
                "Each bike's estimated storage converted to GB and multiplied by your €/GB per month rate. Unlike data, this is recurring: it is what it costs to keep the telemetry this period produced for one further month.",
                'Widening the range raises this for a real reason: more telemetry has accumulated and has to be kept.',
              ],
              formula: 'storage cost per month = Σ (bike storage MB ÷ 1024 × rate €/GB/month)',
            },
            {
              title: 'Per bike per month',
              body: [
                'The blended figure to quote. The data cost is rescaled from the selected range to a 30-day month, the monthly storage cost is added unchanged, and the total is divided by the number of bikes that actually reported.',
                'Storage is deliberately not rescaled: it is already a monthly rate, and dividing it by the range length again would make a 12-month view look almost free.',
                'Bikes that never reported are left out of the divisor, so a warehouse of uninstalled modules cannot flatter the average.',
              ],
              formula: 'per bike/month = (data cost × 30 ÷ range days + storage cost per month) ÷ bikes that reported',
            },
          ],
        },
        {
          title: 'The per-bike table',
          items: [
            {
              title: 'Days online and Hours online',
              body: [
                'Days online counts days with at least one heartbeat hour. Hours online adds up every hour bucket across the range, so its ceiling is 24 × the number of days in the range.',
                'Read together they show the shape of the connection: 30 days online with 60 hours is a bike checking in twice a day, while 30 days with 700 hours is one that is permanently connected.',
              ],
            },
            {
              title: 'Data MB and Quota left MB',
              body: [
                "Data MB is that SIM's traffic within the range. Quota left MB is the remaining lifetime quota 1NCE currently reports for the SIM, which covers the entire life of the SIM and ignores the selected range entirely.",
                'Both are empty when no SIM is linked to the module.',
              ],
            },
            {
              title: 'Storage MB',
              body: [
                "That bike's share of the InfluxDB estimate, from its own datapoint count and its organisation's calibration rate.",
              ],
            },
            {
              title: 'Cost (data + storage/mo)',
              body: [
                'Per bike, its data cost for the period plus its storage cost for one month. It is not rescaled to a month, which makes it the right column for ranking bikes against each other and the wrong one for quoting a monthly price.',
              ],
            },
            {
              title: 'Status',
              body: [
                `Active means the module uploaded recently. "Silent Nd" means it has not uploaded for N days, with N at or above ${GONE_QUIET_DAYS}. "Never reported" means it produced no telemetry inside the range at all.`,
                "Silence is measured from the module's server-side last upload timestamp rather than from InfluxDB, so it stays correct even when the bike's clock is wrong. It also explains an apparent contradiction: a bike can be \"Never reported\" for a short range while not being silent, because it last uploaded just before the range started.",
              ],
            },
            {
              title: 'Clicking a row',
              body: [
                'Opens that bike\'s day-by-day history over the same range: hours online, datapoints and cellular volume per day.',
              ],
            },
          ],
        },
        {
          title: 'How fresh are these numbers?',
          items: [
            {
              title: 'Update cadence',
              body: [
                'Everything is produced by the nightly tasks, so today is usually partial or missing and yesterday is the last complete day. Any of the three tasks can be triggered by hand from the task page.',
                h.calibration_measured_at
                  ? `Storage was last calibrated against real InfluxDB disk usage on ${this.formatDate(h.calibration_measured_at)}.`
                  : 'Storage has never been calibrated against real InfluxDB disk usage.',
              ],
            },
            {
              title: 'When something looks wrong',
              body: [
                'The amber banner at the top of the page is the first place to look. Unsynced SIMs, SIMs that could not be matched to a module, modules with no SIM and unmeasured storage buckets each explain a specific class of missing number.',
              ],
            },
          ],
        },
      ];
    },
  },
  created() {
    const host = document.querySelector('#vue-iot-usage');
    if (host && host.dataset.organisations) {
      try {
        this.organisations = JSON.parse(host.dataset.organisations);
      } catch (e) {
        this.organisations = [];
      }
    }
  },
  mounted() {
    document.addEventListener('dark-mode', this.renderCharts);
    window.addEventListener('resize', this.resizeCharts);
    this.reload();
  },
  beforeUnmount() {
    document.removeEventListener('dark-mode', this.renderCharts);
    window.removeEventListener('resize', this.resizeCharts);
    this.destroyChart('onlineChart');
    this.destroyChart('detailChart');
  },
  methods: {
    query() {
      const params = new URLSearchParams({ days: String(this.days) });
      if (this.org) params.set('org', this.org);
      return params.toString();
    },
    async fetchJson(url) {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Request failed with HTTP ${response.status}`);
      return response.json();
    },
    async reload() {
      this.loading = true;
      this.error = '';
      try {
        const [overview, bikes] = await Promise.all([
          this.fetchJson(`/erp/internal/iot-usage/api/overview/?${this.query()}`),
          this.fetchJson(`/erp/internal/iot-usage/api/bikes/?${this.query()}`),
        ]);
        this.granularity = overview.granularity;
        this.series = overview.series;
        this.kpis = overview.kpis;
        this.health = overview.health;
        this.bikes = bikes.bikes;
        await this.$nextTick();
        this.renderOnlineChart();
      } catch (e) {
        this.error = `Could not load dashboard data: ${e.message}`;
      } finally {
        this.loading = false;
      }
    },
    async openDetail(bike) {
      try {
        this.detail = await this.fetchJson(
          `/erp/internal/iot-usage/api/bike/${encodeURIComponent(bike.imei)}/?${this.query()}`
        );
        await this.$nextTick();
        this.renderDetailChart();
      } catch (e) {
        this.error = `Could not load bike detail: ${e.message}`;
      }
    },
    closeDetail() {
      // The chart's container disappears with the drawer, so dispose it here
      // rather than leaving an instance bound to a detached element.
      this.destroyChart('detailChart');
      this.detail = null;
    },
    renderCharts() {
      this.renderOnlineChart();
      this.renderDetailChart();
    },
    destroyChart(name) {
      if (this[name]) {
        try { this[name].dispose(); } catch (e) { /* already gone */ }
        this[name] = null;
      }
    },
    resizeCharts() {
      ['onlineChart', 'detailChart'].forEach((name) => {
        if (this[name]) this[name].resize();
      });
    },
    initChart(name, el) {
      const echarts = window.echarts;
      if (!el) return null;
      if (!echarts) {
        this.error = 'The chart library did not load. Reload the page to see the graphs.';
        return null;
      }
      if (!this[name] || this[name].isDisposed()) {
        this[name] = echarts.init(el);
      }
      return this[name];
    },
    chartPalette() {
      const dark = this.isDark();
      return {
        text: dark ? '#D1D5DB' : '#4B5563',
        line: dark ? '#4B5563' : '#D1D5DB',
        split: dark ? '#374151' : '#E5E7EB',
        tooltipBg: dark ? '#111827' : '#FFFFFF',
        tooltipBorder: dark ? '#4B5563' : '#D1D5DB',
        tooltipText: dark ? '#F9FAFB' : '#111827',
        pointer: dark ? '#93C5FD' : '#2563EB',
      };
    },
    tooltipBox() {
      const colors = this.chartPalette();
      return {
        trigger: 'axis',
        triggerOn: 'mousemove|click',
        confine: true,
        extraCssText: 'z-index: 60; box-shadow: 0 8px 24px rgba(0,0,0,0.18);',
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: colors.tooltipText, fontSize: 13 },
        axisPointer: {
          type: 'cross',
          snap: true,
          animation: false,
          lineStyle: { color: colors.pointer, width: 1.5, type: 'solid' },
          crossStyle: { color: colors.pointer, width: 1, type: 'dashed' },
          label: {
            show: true,
            backgroundColor: colors.pointer,
            color: '#FFFFFF',
            fontSize: 11,
            padding: [3, 6],
          },
        },
      };
    },
    // Buckets are UTC days and hours, so they are formatted from the raw ISO
    // string: converting to a local Date would shift a day into its neighbour.
    formatBucket(iso, { withYear = false } = {}) {
      const [datePart, timePart] = String(iso).split('T');
      const [year, month, day] = datePart.split('-');
      let label = `${day} ${MONTHS[Number(month) - 1]}`;
      if (withYear) label += ` ${year}`;
      if (timePart) label += ` ${timePart.slice(0, 5)} UTC`;
      return label;
    },
    renderOnlineChart() {
      const chart = this.initChart('onlineChart', this.$refs.onlineChart);
      if (!chart) return;
      const colors = this.chartPalette();
      const buckets = this.series.map((p) => p.t);

      chart.setOption({
        backgroundColor: 'transparent',
        animation: false,
        grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
        tooltip: {
          ...this.tooltipBox(),
          formatter: (params) => {
            const point = Array.isArray(params) ? params[0] : params;
            if (!point) return '';
            const when = this.formatBucket(buckets[point.dataIndex], { withYear: true });
            const count = Number(point.value);
            const noun = count === 1 ? 'bike' : 'bikes';
            return (
              `<div style="font-weight:600;margin-bottom:4px">${when}</div>` +
              `${point.marker} <b>${this.number(count, 0)}</b> ${noun} online`
            );
          },
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: buckets.map((t) => this.formatBucket(t)),
          axisLine: { lineStyle: { color: colors.line } },
          axisTick: { show: false },
          axisLabel: { color: colors.text, hideOverlap: true },
          axisPointer: {
            label: { formatter: (p) => p.value },
          },
        },
        yAxis: {
          type: 'value',
          min: 0,
          minInterval: 1,
          axisLine: { show: false },
          axisLabel: { color: colors.text },
          splitLine: { lineStyle: { color: colors.split, type: 'dashed' } },
          axisPointer: {
            label: { formatter: (p) => `${Math.round(p.value)} bikes` },
          },
        },
        series: [{
          name: 'Bikes online',
          type: 'line',
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 10,
          emphasis: {
            focus: 'series',
            scale: true,
            itemStyle: { borderColor: '#FFFFFF', borderWidth: 2 },
          },
          lineStyle: { width: 2, color: ONLINE_COLOR },
          itemStyle: { color: ONLINE_COLOR },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(37, 99, 235, 0.45)' },
                { offset: 1, color: 'rgba(37, 99, 235, 0.02)' },
              ],
            },
          },
          data: this.series.map((p) => p.online),
        }],
      }, { notMerge: true });
    },
    renderDetailChart() {
      if (!this.detail) return;
      const chart = this.initChart('detailChart', this.$refs.detailChart);
      if (!chart) return;
      const colors = this.chartPalette();
      const history = this.detail.history;

      chart.setOption({
        backgroundColor: 'transparent',
        animation: false,
        grid: { left: 8, right: 8, top: 34, bottom: 40, containLabel: true },
        legend: {
          bottom: 0,
          textStyle: { color: colors.text },
          itemWidth: 14,
          itemHeight: 10,
        },
        tooltip: {
          ...this.tooltipBox(),
          axisPointer: {
            ...this.tooltipBox().axisPointer,
            type: 'shadow',
          },
          formatter: (params) => {
            const items = Array.isArray(params) ? params : [params];
            if (!items.length) return '';
            const when = this.formatBucket(history[items[0].dataIndex]?.date || '', { withYear: true });
            const rows = items.map((p) => {
              const digits = p.seriesName === 'Data MB' ? 3 : 0;
              return `${p.marker} ${p.seriesName}: <b>${this.number(p.value, digits)}</b>`;
            });
            return `<div style="font-weight:600;margin-bottom:4px">${when}</div>${rows.join('<br/>')}`;
          },
        },
        xAxis: {
          type: 'category',
          data: history.map((d) => this.formatBucket(d.date)),
          axisLine: { lineStyle: { color: colors.line } },
          axisTick: { show: false },
          axisLabel: { color: colors.text, hideOverlap: true },
        },
        yAxis: [
          {
            type: 'value',
            name: 'Hours online',
            min: 0,
            max: 24,
            interval: 6,
            nameTextStyle: { color: colors.text, fontSize: 11 },
            axisLine: { show: false },
            axisLabel: { color: colors.text },
            splitLine: { lineStyle: { color: colors.split, type: 'dashed' } },
          },
          {
            type: 'value',
            name: 'Data MB',
            min: 0,
            nameTextStyle: { color: colors.text, fontSize: 11 },
            axisLine: { show: false },
            axisLabel: { color: colors.text },
            splitLine: { show: false },
          },
        ],
        series: [
          {
            name: 'Hours online',
            type: 'bar',
            yAxisIndex: 0,
            itemStyle: { color: ONLINE_COLOR, borderRadius: [2, 2, 0, 0] },
            data: history.map((d) => d.hours_online),
          },
          {
            name: 'Data MB',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 8,
            emphasis: { scale: true, itemStyle: { borderColor: '#FFFFFF', borderWidth: 2 } },
            lineStyle: { width: 2, color: DATA_COLOR },
            itemStyle: { color: DATA_COLOR },
            data: history.map((d) => Number(d.data_mb.toFixed(3))),
          },
        ],
      }, { notMerge: true });
    },
    dataCostOf(bike) {
      return (bike.data_mb || 0) * (this.rates.dataPerMb || 0);
    },
    storageMonthlyCostOf(bike) {
      return ((bike.storage_mb || 0) / MB_PER_GB) * (this.rates.storagePerGbMonth || 0);
    },
    costOf(bike) {
      return this.dataCostOf(bike) + this.storageMonthlyCostOf(bike);
    },
    sortBy(key) {
      if (this.sort.key === key) {
        this.sort.desc = !this.sort.desc;
      } else {
        this.sort = { key, desc: true };
      }
    },
    sortValue(bike, key) {
      if (key === 'cost') return this.costOf(bike);
      if (key === 'status') return this.statusLabel(bike);
      return bike[key];
    },
    statusLabel(bike) {
      if (bike.never_seen) return 'Never reported';
      if (bike.gone_quiet) return `Silent ${bike.days_silent}d`;
      return 'Active';
    },
    statusClass(bike) {
      const base = 'px-2 py-0.5 rounded text-xs font-medium ';
      if (bike.never_seen) return `${base} bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200`;
      if (bike.gone_quiet) return `${base} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200`;
      return `${base} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200`;
    },
    exportCsv() {
      const header = [
        'frame_number', 'imei', 'organisation', 'iccid', 'days_online', 'hours_online',
        'data_mb', 'storage_mb', 'quota_remaining_mb', 'data_cost_eur',
        'storage_cost_eur_per_month', 'last_seen', 'status',
      ];
      const lines = this.visibleBikes.map((bike) => [
        bike.frame_number, bike.imei, bike.organisation, bike.iccid, bike.days_online,
        bike.hours_online, bike.data_mb, bike.storage_mb,
        bike.quota_remaining_mb === null ? '' : bike.quota_remaining_mb,
        this.dataCostOf(bike).toFixed(4), this.storageMonthlyCostOf(bike).toFixed(4),
        bike.last_seen || '', this.statusLabel(bike),
      ]);
      const csv = [header, ...lines]
        .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `iot-usage-${this.days}d.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    number(value, digits = 0) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    },
    money(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
      return `€${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString();
    },
    isDark() {
      return document.documentElement.classList.contains('dark');
    },
  },
};
</script>

<style scoped>
</style>
